import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { config } from '../config';
import { authService } from '../services/authService';
import { useZoneStore } from './zoneStore';
import { useTabStore, isCrossZoneTab, type TabType } from './tabStore';
import powerFlowService from '../services/powerFlowService';

// Types for load flow results (previously from networkUpdatesService)
export interface LoadFlowResult {
  zoneId: string;
  timestamp: string;
  success: boolean;
  convergenceIterations?: number;
  busResults?: BusResult[];
  lineResults?: LineResult[];
  trafoResults?: TrafoResult[];
  error?: string;
}

export interface BusResult {
  busIndex: number;
  elementId?: string;
  voltagePu: number;
  voltageAngleDeg: number;
  pMw?: number;
  qMvar?: number;
}

export interface LineResult {
  lineIndex: number;
  elementId?: string;
  pFromMw: number;
  qFromMvar: number;
  pToMw: number;
  qToMvar: number;
  loadingPercent: number;
}

export interface TrafoResult {
  trafoIndex: number;
  elementId?: string;
  pHvMw: number;
  qHvMvar: number;
  pLvMw: number;
  qLvMvar: number;
  loadingPercent: number;
}

interface LoadFlowState {
  // Current load flow result
  result: LoadFlowResult | null;
  lastUpdated: Date | null;

  // Loading state for initial fetch
  isLoading: boolean;
  error: string | null;

  // Quick access maps for looking up results by element
  busResultsByIndex: Map<number, BusResult>;
  lineResultsByIndex: Map<number, LineResult>;
  trafoResultsByIndex: Map<number, TrafoResult>;
}

interface LoadFlowActions {
  setResult: (result: LoadFlowResult) => void;
  clearResult: () => void;
  fetchLatestResult: (zoneId: string) => Promise<void>;
  getBusVoltage: (busIndex: number) => number | null;
  getLineLoading: (lineIndex: number) => number | null;
  getTrafoLoading: (trafoIndex: number) => number | null;
}

type LoadFlowStore = LoadFlowState & LoadFlowActions;

export const useLoadFlowStore = create<LoadFlowStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      result: null,
      lastUpdated: null,
      isLoading: false,
      error: null,
      busResultsByIndex: new Map(),
      lineResultsByIndex: new Map(),
      trafoResultsByIndex: new Map(),

      // Actions
      setResult: (result: LoadFlowResult) => {
        // Build lookup maps for fast access
        const busResultsByIndex = new Map<number, BusResult>();
        const lineResultsByIndex = new Map<number, LineResult>();
        const trafoResultsByIndex = new Map<number, TrafoResult>();

        result.busResults?.forEach(bus => {
          busResultsByIndex.set(bus.busIndex, bus);
        });

        result.lineResults?.forEach(line => {
          lineResultsByIndex.set(line.lineIndex, line);
        });

        result.trafoResults?.forEach(trafo => {
          trafoResultsByIndex.set(trafo.trafoIndex, trafo);
        });

        set({
          result,
          lastUpdated: new Date(),
          busResultsByIndex,
          lineResultsByIndex,
          trafoResultsByIndex,
          error: null
        });

      },

      clearResult: () => {
        set({
          result: null,
          lastUpdated: null,
          busResultsByIndex: new Map(),
          lineResultsByIndex: new Map(),
          trafoResultsByIndex: new Map(),
          error: null
        });
      },

      fetchLatestResult: async (zoneId: string) => {
        set({ isLoading: true, error: null });

        try {
          const token = authService.getToken();
          const headers: HeadersInit = {
            'Content-Type': 'application/json'
          };
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }

          const response = await fetch(
            `${config.apiBaseUrl}/api/zones/${zoneId}/load-flow/latest`,
            { headers }
          );

          if (!response.ok) {
            throw new Error(`Failed to fetch load flow result: ${response.status}`);
          }

          const data = await response.json();

          // Discard stale responses after a zone switch (zone switch clears the store first;
          // applying an in-flight result from the old zone would repopulate it)
          if (useZoneStore.getState().activeZoneId !== zoneId) {
            set({ isLoading: false });
            return;
          }

          if (data.hasResult && data.data?.result) {
            get().setResult(data.data.result);
          }

          set({ isLoading: false });
        } catch (error) {
          console.error('[LoadFlowStore] Error fetching latest result:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      },

      getBusVoltage: (busIndex: number): number | null => {
        const bus = get().busResultsByIndex.get(busIndex);
        return bus?.voltagePu ?? null;
      },

      getLineLoading: (lineIndex: number): number | null => {
        const line = get().lineResultsByIndex.get(lineIndex);
        return line?.loadingPercent ?? null;
      },

      getTrafoLoading: (trafoIndex: number): number | null => {
        const trafo = get().trafoResultsByIndex.get(trafoIndex);
        return trafo?.loadingPercent ?? null;
      }
    }),
    { name: 'load-flow-store' }
  )
);

// REST-based polling for load flow results (replaced SSE)
const POLL_INTERVAL_MS = 10000; // 10 seconds
let pollIntervalId: ReturnType<typeof setInterval> | null = null;
let zoneUnsubscribe: (() => void) | null = null;
let tabUnsubscribe: (() => void) | null = null;
let lastActiveZoneId: string | null = null;

// The cross-zone tabs (dashboard, …) render zone statistics only; the hidden map underneath
// does not need a fresh power calculation every 10 s (216 KB JSON + a full marker/cable
// reconciliation of the previously selected zone). Polling pauses on those tabs
// (tabStore.CROSS_ZONE_TAB_TYPES) and catches up with an immediate fetch when the user
// leaves them.
const activeTabTypeOf = (s: { tabs: { id: string; type: TabType }[]; activeTabId: string }) =>
  s.tabs.find(tab => tab.id === s.activeTabId)?.type;
const isCrossZoneTabActive = () => isCrossZoneTab(activeTabTypeOf(useTabStore.getState()));

/**
 * Initialize load flow polling for the active zone.
 * - Subscribes to zone changes for immediate fetch on zone switch
 * - Polls every 10 seconds for updates (synchronized with the measurement pool interval)
 */
export function initLoadFlowPolling() {
  // Clean up any existing subscriptions
  cleanupLoadFlowPolling();

  // Initial fetch if zone is already selected — both load flow results
  // AND power calculation (segment data needed for cable flow animation)
  const initialZoneId = useZoneStore.getState().activeZoneId;
  lastActiveZoneId = initialZoneId;
  if (initialZoneId) {
    useLoadFlowStore.getState().fetchLatestResult(initialZoneId);
    powerFlowService.fetchAndUpdate();
  }

  // Subscribe to zone changes (standard Zustand subscribe)
  zoneUnsubscribe = useZoneStore.subscribe((state) => {
    const activeZoneId = state.activeZoneId;

    // Only react when activeZoneId actually changes
    if (activeZoneId !== lastActiveZoneId) {
      lastActiveZoneId = activeZoneId;

      if (activeZoneId) {
        // Immediately fetch both load flow results and segment power data
        useLoadFlowStore.getState().fetchLatestResult(activeZoneId);
        powerFlowService.fetchAndUpdate();
      } else {
        // Clear result when no zone selected
        useLoadFlowStore.getState().clearResult();
      }
    }
  });

  // Leaving a cross-zone tab: catch up immediately instead of waiting for the next tick
  tabUnsubscribe = useTabStore.subscribe((state, prev) => {
    const wasCrossZone = isCrossZoneTab(activeTabTypeOf(prev));
    const isCrossZone = isCrossZoneTab(activeTabTypeOf(state));
    const activeZoneId = useZoneStore.getState().activeZoneId;
    if (wasCrossZone && !isCrossZone && activeZoneId) {
      useLoadFlowStore.getState().fetchLatestResult(activeZoneId);
      powerFlowService.fetchAndUpdate();
    }
  });

  // Start polling
  startPolling();

}

function startPolling() {
  // Clear any existing interval
  if (pollIntervalId) {
    clearInterval(pollIntervalId);
  }

  pollIntervalId = setInterval(async () => {
    const activeZoneId = useZoneStore.getState().activeZoneId;
    if (activeZoneId && !isCrossZoneTabActive()) {
      try {
        await useLoadFlowStore.getState().fetchLatestResult(activeZoneId);
        await powerFlowService.fetchAndUpdate();
      } catch (error) {
        console.warn('[LoadFlowStore] Polling error:', error);
      }
    }
  }, POLL_INTERVAL_MS);
}

/**
 * Clean up load flow polling and subscriptions.
 */
export function cleanupLoadFlowPolling() {
  if (pollIntervalId) {
    clearInterval(pollIntervalId);
    pollIntervalId = null;
  }

  if (zoneUnsubscribe) {
    zoneUnsubscribe();
    zoneUnsubscribe = null;
  }

  if (tabUnsubscribe) {
    tabUnsubscribe();
    tabUnsubscribe = null;
  }

  lastActiveZoneId = null;
}

// Legacy aliases for backward compatibility (can be removed later)
export const initLoadFlowSubscription = initLoadFlowPolling;
export const cleanupLoadFlowSubscription = cleanupLoadFlowPolling;
